// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Autocomplete,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Dosage, MedicationStatement, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { type JSX, useEffect, useState } from 'react';

const RXNORM_VALUESET = 'http://www.nlm.nih.gov/research/umls/rxnorm/vs';

const MEDICATION_UNITS = new Set(['mg', 'mcg', 'g', 'ml', 'l', 'iu', 'meq']);

function normalizeMedicationDisplay(text: string): string {
  return text
    .toLowerCase()
    .split(/(\s+)/)
    .map((part) => {
      if (/^\s+$/.test(part) || part === '') {
        return part;
      }
      if (MEDICATION_UNITS.has(part)) {
        return part;
      }
      if (/\d/.test(part)) {
        return part;
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('');
}

const UNIT_OPTIONS = [
  { value: 'mg', label: 'mg' },
  { value: 'mcg', label: 'mcg' },
  { value: 'g', label: 'g' },
  { value: 'mL', label: 'mL' },
  { value: 'tablet', label: 'tablet' },
  { value: 'capsule', label: 'capsule' },
  { value: 'drop', label: 'drop' },
  { value: 'spray', label: 'spray' },
  { value: 'unit', label: 'unit' },
];

const FREQUENCY_OPTIONS = [
  { value: 'once-daily', label: 'Once daily' },
  { value: 'twice-daily', label: 'Twice daily' },
  { value: 'three-times-daily', label: 'Three times daily' },
  { value: 'four-times-daily', label: 'Four times daily' },
  { value: 'every-morning', label: 'Every morning' },
  { value: 'every-evening', label: 'Every evening' },
  { value: 'every-bedtime', label: 'At bedtime' },
  { value: 'as-needed', label: 'As needed' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'other', label: 'Other' },
];

const FREQUENCY_SYSTEM = 'http://polarissleep.com/medication-frequency';

function MedicationTitleInput({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (val: string) => void;
  label?: string;
}): JSX.Element {
  const medplum = useMedplum();
  const [search, setSearch] = useState(value);
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    setSearch(value);
  }, [value]);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setOptions([]);
      return;
    }
    medplum
      .valueSetExpand({ url: RXNORM_VALUESET, filter: debouncedSearch, count: 10 })
      .then((vs) => {
        setOptions(
          (vs.expansion?.contains ?? [])
            .map((c) => normalizeMedicationDisplay(c.display ?? c.code ?? ''))
            .filter(Boolean)
        );
      })
      .catch((err) => {
        console.error('RxNorm lookup failed', err);
        setOptions([]);
      });
  }, [debouncedSearch, medplum]);

  return (
    <Autocomplete
      label={label}
      value={search}
      data={options}
      placeholder="Search medication..."
      filter={({ options }) => options}
      onChange={(val) => {
        setSearch(val);
        onChange(val);
      }}
    />
  );
}

interface MedicationRow {
  id?: string;
  title: string;
  dosage: string;
  unit: string;
  frequency: string;
  frequencyOther: boolean;
  resource?: MedicationStatement;
  dirty?: boolean;
}

function getDosageText(statement: MedicationStatement): { dosage: string; unit: string; frequency: string } {
  const dose = statement.dosage?.[0];
  const doseQuantity = dose?.doseAndRate?.[0]?.doseQuantity;
  const frequency = dose?.timing?.code?.coding?.[0]?.code ?? dose?.timing?.code?.text ?? '';
  return {
    dosage: doseQuantity?.value !== undefined ? String(doseQuantity.value) : '',
    unit: doseQuantity?.unit ?? doseQuantity?.code ?? '',
    frequency,
  };
}

const OTHER_FREQUENCY_VALUE = 'other';

function buildDosage(row: MedicationRow): Dosage[] | undefined {
  const amount = Number.parseFloat(row.dosage);
  if (!row.dosage && !row.unit && !row.frequency) {
    return undefined;
  }
  return [
    {
      doseAndRate:
        row.dosage && !Number.isNaN(amount)
          ? [
              {
                doseQuantity: {
                  value: amount,
                  unit: row.unit || undefined,
                  code: row.unit || undefined,
                },
              },
            ]
          : undefined,
      timing: row.frequency
        ? {
            code: {
              coding: [
                {
                  system: FREQUENCY_SYSTEM,
                  code: row.frequency,
                  display: FREQUENCY_OPTIONS.find((o) => o.value === row.frequency)?.label ?? row.frequency,
                },
              ],
            },
          }
        : undefined,
    },
  ];
}

function toRows(statements: MedicationStatement[]): MedicationRow[] {
  return statements.map((s) => {
    const dosageText = getDosageText(s);
    return {
      id: s.id,
      title: s.medicationCodeableConcept?.text ?? s.medicationCodeableConcept?.coding?.[0]?.display ?? '',
      ...dosageText,
      frequencyOther:
        !!dosageText.frequency &&
        dosageText.frequency !== OTHER_FREQUENCY_VALUE &&
        !FREQUENCY_OPTIONS.some((o) => o.value === dosageText.frequency),
      resource: s,
    };
  });
}

export interface MedicationsSectionProps {
  medications: MedicationStatement[];
  loading: boolean;
  onChanged: (medications: MedicationStatement[]) => void;
  onRefresh: () => void;
}

export function MedicationsSection({
  medications,
  loading,
  onChanged,
  onRefresh,
}: MedicationsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;

  const [rows, setRows] = useState<MedicationRow[]>(toRows(medications));
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    setRows(toRows(medications));
  }, [medications]);

  const updateRow = (index: number, patch: Partial<MedicationRow>): void => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch, dirty: true } : r)));
  };

  const addRow = (): void => {
    setRows((prev) => [
      ...prev,
      { title: '', dosage: '', unit: '', frequency: '', frequencyOther: false, dirty: true },
    ]);
  };

  const removeRow = async (index: number): Promise<void> => {
    const row = rows[index];
    if (row.id) {
      try {
        await medplum.deleteResource('MedicationStatement', row.id);
      } catch (err) {
        showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
        return;
      }
    }
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    onChanged(next.filter((r) => r.resource).map((r) => r.resource as MedicationStatement));
    onRefresh();
  };

  const saveRow = async (index: number): Promise<void> => {
    if (!profile?.id) {
      return;
    }
    const row = rows[index];
    if (!row.title.trim()) {
      showNotification({ color: 'red', message: 'Medication title is required' });
      return;
    }
    if (row.frequencyOther && !row.frequency.trim()) {
      showNotification({ color: 'red', message: 'Enter a frequency for Other' });
      return;
    }
    setSaving(index);
    try {
      const base: MedicationStatement = row.resource ?? {
        resourceType: 'MedicationStatement',
        status: 'active',
        subject: createReference(profile),
      };
      const next: MedicationStatement = {
        ...base,
        status: base.status ?? 'active',
        subject: base.subject ?? createReference(profile),
        medicationCodeableConcept: {
          text: row.title.trim(),
          coding: base.medicationCodeableConcept?.coding,
        },
        dosage: buildDosage(row),
      };
      const saved = row.id
        ? await medplum.updateResource<MedicationStatement>({ ...next, id: row.id })
        : await medplum.createResource<MedicationStatement>(next);
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, id: saved.id, resource: saved, dirty: false } : r)));
      onRefresh();
      showNotification({ color: 'green', message: 'Medication saved' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(null);
    }
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Medications</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={addRow}>
          Add New
        </Button>
      </Group>

      {(() => {
        if (loading) {
          return (
            <Stack align="center" py="md">
              <Loader size="sm" />
            </Stack>
          );
        }
        if (rows.length === 0) {
          return (
            <Text c="dimmed" ta="center" py="md">
              No medications on file.
            </Text>
          );
        }
        return (
          <Stack gap="sm">
            {rows.map((row, index) => (
              <Grid key={row.id ?? `new-${index}`} align="flex-end">
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <MedicationTitleInput
                    label={index === 0 ? 'Medication Title' : undefined}
                    value={row.title}
                    onChange={(val) => updateRow(index, { title: val })}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 4, md: 2 }}>
                  <TextInput
                    label={index === 0 ? 'Dosage' : undefined}
                    value={row.dosage}
                    onChange={(e) => updateRow(index, { dosage: e.currentTarget.value })}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 4, md: 2 }}>
                  <Select
                    label={index === 0 ? 'Unit' : undefined}
                    data={UNIT_OPTIONS}
                    value={row.unit || null}
                    onChange={(v) => updateRow(index, { unit: v ?? '' })}
                    searchable
                    clearable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 4, md: 3 }}>
                  <Select
                    label={index === 0 ? 'Frequency' : undefined}
                    data={FREQUENCY_OPTIONS}
                    value={row.frequencyOther ? OTHER_FREQUENCY_VALUE : row.frequency || null}
                    onChange={(v) =>
                      updateRow(index, {
                        frequency: v === OTHER_FREQUENCY_VALUE ? '' : (v ?? ''),
                        frequencyOther: v === OTHER_FREQUENCY_VALUE,
                      })
                    }
                    clearable
                  />
                  {row.frequencyOther && (
                    <TextInput
                      mt="xs"
                      placeholder="Enter frequency"
                      value={row.frequency}
                      onChange={(event) => updateRow(index, { frequency: event.currentTarget.value })}
                      error={!row.frequency.trim() ? 'Frequency is required' : undefined}
                    />
                  )}
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 1 }}>
                  <Group gap={4}>
                    {row.dirty && (
                      <Button size="xs" onClick={() => saveRow(index)} loading={saving === index}>
                        Save
                      </Button>
                    )}
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      aria-label="Remove medication"
                      onClick={() => removeRow(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Grid.Col>
              </Grid>
            ))}
          </Stack>
        );
      })()}
    </Paper>
  );
}
